import { supabase } from '@/integrations/supabase/client';
import { CreateServiceRequestData, CreateServiceRequestResponse, ServiceRequest, JobProposal, WebhookJobProposal, ProposalFeedbackRequest, ProposalFeedbackResponse } from '@/types/serviceRequest';

export class ServiceRequestService {
  /**
   * Create a new service request
   */
  static async createServiceRequest(data: CreateServiceRequestData): Promise<CreateServiceRequestResponse> {
    try {
      // Prepare files data for upload if attachments exist
      const filesData = data.attachments ? await Promise.all(
        data.attachments.map(async (file, index) => {
          const fileToBase64 = (file: File): Promise<string> => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = error => reject(error);
            });
          };

          return {
            data: await fileToBase64(file),
            originalFilename: file.name,
            fileSize: file.size,
            mimeType: file.type,
            displayOrder: index + 1
          };
        })
      ) : undefined;

      // Call the edge function
      const { data: response, error } = await supabase.functions.invoke('submit-service-request', {
        body: {
          community_id: data.community_id,
          original_request: data.original_request,
          town: data.town,
          urgency: data.urgency,
          service_request_id: data.service_request_id,
          attachments: filesData
        }
      });

      if (error) {
        console.error('Service request creation error:', error);
        return {
          success: false,
          error: error.message || 'Failed to create service request'
        };
      }

      return response;
    } catch (error) {
      console.error('Service request creation error:', error);
      return {
        success: false,
        error: 'Failed to create service request'
      };
    }
  }

  /**
   * Get service requests for a specific user by ID
   */
  static async getServiceRequestsById(userId: string): Promise<ServiceRequest[]> {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          attachments:service_request_attachments(*),
          proposals:job_proposals(
            *,
            status,
            business:businesses(
              id,
              name,
              slug,
              company_logo,
              company_banner_url,
              user_id
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching service requests:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching service requests:', error);
      return [];
    }
  }

  /**
   * Get a specific service request by ID
   */
  static async getServiceRequest(id: string): Promise<ServiceRequest | null> {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          attachments:service_request_attachments(*),
          proposals:job_proposals(
            *,
            business:businesses(
              id,
              name,
              slug,
              company_logo,
              user_id
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching service request:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching service request:', error);
      return null;
    }
  }

  /**
   * Get a specific service request with detailed proposals and business data
   */
  static async getServiceRequestWithProposals(id: string): Promise<ServiceRequest | null> {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          attachments:service_request_attachments(*),
          proposals:job_proposals(
            *,
            detailed_quote:quotes(
              *,
              quote_items(*)
            ),
            business:businesses(
              id,
              name,
              slug,
              company_logo,
              company_banner_url,
              city,
              postal_code,
              user_id
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching service request with proposals:', error);
        return null;
      }

      if (!data) {
        return null;
      }

      // If there are proposals, fetch reviews for each business
      if (data.proposals && data.proposals.length > 0) {
        const businessIds = data.proposals
          .map(proposal => proposal.business?.id)
          .filter(Boolean) as string[];

        if (businessIds.length > 0) {
          const { data: reviewsData, error: reviewsError } = await supabase
            .from('reviews')
            .select('business_id, rating')
            .eq('status', 'Published')
            .in('business_id', businessIds);

          if (!reviewsError && reviewsData) {
            // Calculate ratings for each business
            const businessRatings = new Map();
            reviewsData.forEach(review => {
              const businessId = review.business_id;
              if (!businessRatings.has(businessId)) {
                businessRatings.set(businessId, []);
              }
              businessRatings.get(businessId).push(review.rating);
            });

            // Add reviews to each business in proposals
            data.proposals = data.proposals.map(proposal => ({
              ...proposal,
              business: proposal.business ? {
                ...proposal.business,
                reviews: businessRatings.has(proposal.business.id) 
                  ? businessRatings.get(proposal.business.id).map((rating: number) => ({ rating }))
                  : []
              } : undefined
            }));
          }
        }
      }

      return data;
    } catch (error) {
      console.error('Error fetching service request with proposals:', error);
      return null;
    }
  }

  /**
   * Update service request status
   */
  static async updateServiceRequestStatus(
    id: string, 
    status: ServiceRequest['status']
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('service_requests')
        .update({ status })
        .eq('id', id);

      if (error) {
        console.error('Error updating service request status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating service request status:', error);
      return false;
    }
  }

  /**
   * Get job proposals for a service request with calculated ratings
   */
  static async getJobProposals(serviceRequestId: string): Promise<JobProposal[]> {
    try {
      // First, fetch job proposals with basic business data (no reviews)
      const { data: proposalsData, error } = await supabase
        .from('job_proposals')
        .select(`
          *,
          business:businesses(
            id,
            name,
            slug,
            company_logo,
            company_banner_url,
            city,
            postal_code,
            user_id
          )
        `)
        .eq('service_request_id', serviceRequestId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching job proposals:', error);
        return [];
      }

      if (!proposalsData || proposalsData.length === 0) {
        return [];
      }

      // Get business IDs for review calculation
      const businessIds = proposalsData.map(proposal => proposal.business?.id).filter(Boolean) as string[];

      if (businessIds.length === 0) {
        return proposalsData;
      }

      // Fetch published reviews separately (like useServices does)
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('business_id, rating')
        .eq('status', 'Published')
        .in('business_id', businessIds);

      if (reviewsError) {
        console.error('Reviews query error:', reviewsError);
        // Return proposals without ratings rather than failing completely
        return proposalsData;
      }

      // Calculate ratings and review counts for each business (like useServices does)
      const businessRatings = new Map();
      const businessReviewCounts = new Map();

      if (reviewsData) {
        reviewsData.forEach(review => {
          const businessId = review.business_id;
          
          if (!businessRatings.has(businessId)) {
            businessRatings.set(businessId, []);
            businessReviewCounts.set(businessId, 0);
          }
          
          businessRatings.get(businessId).push(review.rating);
          businessReviewCounts.set(businessId, businessReviewCounts.get(businessId) + 1);
        });
      }

      // Calculate average ratings
      const businessAverageRatings = new Map();
      businessRatings.forEach((ratings, businessId) => {
        const average = ratings.reduce((sum: number, rating: number) => sum + rating, 0) / ratings.length;
        businessAverageRatings.set(businessId, average);
      });

      // Map proposals with calculated ratings
      return proposalsData.map(proposal => ({
        ...proposal,
        business: proposal.business ? {
          ...proposal.business,
          // Add calculated ratings as reviews array to match existing interface
          reviews: businessRatings.has(proposal.business.id) 
            ? businessRatings.get(proposal.business.id).map((rating: number) => ({ rating }))
            : []
        } : undefined
      }));

    } catch (error) {
      console.error('Error fetching job proposals:', error);
      return [];
    }
  }

  /**
   * Submit user feedback on a job proposal
   */
  static async submitProposalFeedback(request: ProposalFeedbackRequest): Promise<ProposalFeedbackResponse> {
    try {
      const { data: response, error } = await supabase.functions.invoke('submit-proposal-feedback', {
        body: request
      });

      if (error) {
        console.error('Proposal feedback submission error:', error);
        return {
          success: false,
          error: error.message || 'Failed to submit proposal feedback'
        };
      }

      return response;
    } catch (error) {
      console.error('Proposal feedback submission error:', error);
      return {
        success: false,
        error: 'Failed to submit proposal feedback'
      };
    }
  }
}